import moment from "moment-hijri";

const RETIREMENT_AGE = { 1: 60, 2: 60, 3: 75, 4: 60, 5: 60, 6: 60 };

const YES_WORDS = ["نعم", "اي", "ايه", "ايوه", "اه", "اجل", "يب", "مدعوم", "صحيح", "تمام", "ok", "yes"];
const NO_WORDS = ["لا", "لأ", "مو", "غير مدعوم", "مو مدعوم", "no"];
const SKIP_WORDS = ["تخطي", "تجاوز", "بعدين", "skip", "ما ادري", "مادري", "مو مهم"];

export function normalizeDigits(value) {
  return String(value || "").replace(/[٠-٩]/g, (digit) => "٠١٢٣٤٥٦٧٨٩".indexOf(digit));
}

export function normalizeArabic(value) {
  return normalizeDigits(value)
    .toLowerCase()
    .replace(/[أإآ]/g, "ا")
    .replace(/ة/g, "ه")
    .replace(/ى/g, "ي")
    .replace(/[\u064B-\u0652]/g, "")
    .replace(/[^\u0600-\u06ffa-z0-9\s]/gi, " ")
    .split(/\s+/)
    .map((word) => word.replace(/^ال/, ""))
    .filter((word) => word && !["بنك", "مصرف", "شركه"].includes(word))
    .join(" ")
    .trim();
}

export function parseAmount(text) {
  const normalized = normalizeDigits(text).replace(/,/g, "").replace(/[٠-٩]/g, (digit) => "٠١٢٣٤٥٦٧٨٩".indexOf(digit));
  const thousands = normalized.match(/(\d+(?:\.\d+)?)\s*(الف|ألف|آلاف|الاف)/);
  if (thousands) return Math.round(parseFloat(thousands[1]) * 1000);
  const number = normalized.match(/(\d+(?:\.\d+)?)/);
  return number ? Number(number[1]) : null;
}

export function parseDateAnswer(text) {
  const raw = normalizeDigits(text).replace(/[/.]/g, "-");
  const iso = raw.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
  const dmy = raw.match(/(\d{1,2})-(\d{1,2})-(\d{4})/);
  if (!iso && !dmy) return null;
  const year = iso ? Number(iso[1]) : Number(dmy[3]);
  const month = iso ? Number(iso[2]) : Number(dmy[2]);
  const day = iso ? Number(iso[3]) : Number(dmy[1]);
  const padded = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  if (year >= 1700) {
    const miladi = moment(padded, "YYYY-MM-DD");
    if (!miladi.isValid()) return null;
    return {
      date_type: 2,
      hijri: formatLatin(miladi, "iYYYY-iMM-iDD"),
      miladi: formatLatin(miladi, "YYYY-MM-DD"),
    };
  }
  const hijri = moment(padded, "iYYYY-iMM-iDD");
  if (!hijri.isValid() || formatLatin(hijri, "iYYYY-iMM-iDD") !== padded) return null;
  return {
    date_type: 1,
    hijri: formatLatin(hijri, "iYYYY-iMM-iDD"),
    miladi: formatLatin(hijri, "YYYY-MM-DD"),
  };
}

function formatLatin(value, pattern) {
  return value.clone().locale("en").format(pattern);
}

function isYes(text) {
  const normalized = normalizeArabic(text);
  return YES_WORDS.includes(normalized) || normalized.startsWith("نعم");
}

function normalizeLoose(value) {
  return normalizeDigits(value)
    .toLowerCase()
    .replace(/[أإآ]/g, "ا")
    .replace(/ة/g, "ه")
    .replace(/ى/g, "ي")
    .replace(/[\u064B-\u0652]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function isNo(text) {
  const normalized = normalizeArabic(text);
  return NO_WORDS.includes(normalized) || normalized === "لا" || normalized.startsWith("لا ") || normalized.startsWith("غير مدعوم");
}

function isSkip(text) {
  const normalized = normalizeArabic(text);
  return SKIP_WORDS.includes(normalized) || normalized.startsWith("تخطي");
}

function wantsCalculate(text) {
  return /احسب|طلع النتيجه|كمل الحساب/.test(normalizeArabic(text));
}

export function readDurationChange(text) {
  const normalized = normalizeArabic(text);
  if (!normalized.includes("مده")) return null;
  const years = parseAmount(text);
  if (!years || years < 1 || years > 40) return { years: null };
  return { years };
}

function labelOf(item, keys) {
  for (let i = 0; i < keys.length; i += 1) {
    if (item && item[keys[i]]) return String(item[keys[i]]);
  }
  return "";
}

function matchCatalog(text, items, labelFn) {
  const normalized = normalizeArabic(text);
  if (!normalized) return [];
  const scored = items
    .map((item) => {
      const label = labelFn(item);
      const itemText = normalizeArabic(label);
      let score = 0;
      if (itemText && itemText === normalized) score = 3;
      else if (itemText && (itemText.includes(normalized) || normalized.includes(itemText))) score = 2;
      return { item, label, score };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score);
  if (!scored.length) return [];
  const best = scored[0].score;
  return scored.filter((entry) => entry.score === best);
}

function filled(value) {
  if (value === null || value === undefined || value === "") return false;
  if (typeof value === "number" && Number.isNaN(value)) return false;
  return true;
}

function isAgree(text) {
  const normalized = normalizeArabic(text);
  return isYes(text) || ["موافق", "نكمل", "اوافق", "خليها", "كما هو", "نفسه"].includes(normalized);
}

function isChange(text) {
  const normalized = normalizeArabic(text);
  return normalized === "تغيير" || normalized === "غير" || normalized === "تعديل" || normalized.startsWith("تغيير") || normalized.startsWith("ابي اغير") || normalized.startsWith("ابغى اغير");
}

const CONFIRM_LABEL = {
  bank: "جهة التمويل",
  salary: "الراتب",
  supported: "الدعم",
  support_amount: "قسط الدعم",
  work: "جهة العمل",
  rank: "الرتبة",
  birth: "تاريخ الميلاد",
  hiring: "تاريخ التعيين",
  product: "المنتج",
  property_amount: "قيمة العقار",
  property_status: "حالة العقار",
  residence: "نوع المسكن",
  obligations: "الالتزامات",
  obligations_installment: "قسط الالتزامات",
  obligations_months: "أشهر الالتزامات",
};

const STEP_KEYS = {
  bank: ["bank_id"],
  salary: ["salary"],
  supported: ["residential_support", "support_amount", "add_support_installment_to_salary"],
  support_amount: ["support_amount"],
  work: ["job_position"],
  rank: ["military_rank"],
  birth: ["birthDate", "birthDateMiladi"],
  hiring: ["hiringDate", "hiringDateMiladi"],
  product: ["product_code"],
  property_amount: ["property_amount"],
  property_status: ["property_completed"],
  residence: ["residence_type"],
  obligations: ["has_obligations", "hasObligations", "obligations_installment", "remaining_obligations_months"],
  obligations_installment: ["obligations_installment"],
  obligations_months: ["remaining_obligations_months"],
};

function confirmSteps(saved) {
  const steps = [];
  if (!saved || typeof saved !== "object") return steps;
  if (filled(saved.bank_id)) steps.push("bank");
  if (filled(saved.salary) || saved.salary === 0) steps.push("salary");
  if (filled(saved.residential_support) || saved.residential_support === 0) steps.push("supported");
  if (Number(saved.residential_support) === 1 && (filled(saved.support_amount) || saved.support_amount === 0)) {
    steps.push("support_amount");
  }
  if (filled(saved.job_position)) steps.push("work");
  if (Number(saved.job_position) === 1 && filled(saved.military_rank)) steps.push("rank");
  if (filled(saved.birthDate)) steps.push("birth");
  if (Number(saved.job_position) !== 3 && filled(saved.hiringDate)) steps.push("hiring");
  if (filled(saved.product_code)) steps.push("product");
  if (filled(saved.property_amount)) steps.push("property_amount");
  if (filled(saved.property_completed) || saved.property_completed === 0) steps.push("property_status");
  if (filled(saved.residence_type)) steps.push("residence");
  if (Object.prototype.hasOwnProperty.call(saved, "has_obligations") || Object.prototype.hasOwnProperty.call(saved, "hasObligations")) {
    steps.push("obligations");
  }
  if (Number(saved.has_obligations) === 1 && (filled(saved.obligations_installment) || saved.obligations_installment === 0)) {
    steps.push("obligations_installment");
  }
  if (Number(saved.has_obligations) === 1 && (filled(saved.remaining_obligations_months) || saved.remaining_obligations_months === 0)) {
    steps.push("obligations_months");
  }
  return steps;
}

export function createFundCalcSession({ banks = [], jobs = [], ranks = [], products = [], known = {}, saved = {}, todayHijri = "" } = {}) {
  const catalogs = { banks, jobs, ranks, products };
  const collected = seed(known, todayHijri, saved);
  const pending = new Set(confirmSteps(saved));
  let current = null;
  let phase = "ask";

  function activeBanks() {
    return (catalogs.banks || []).filter((bank) => bank.active !== false);
  }

  function jobItems() {
    return catalogs.jobs || [];
  }

  function rankItems() {
    return catalogs.ranks || [];
  }

  function productItems() {
    return catalogs.products || [];
  }

  function question(step) {
    if (String(step).startsWith("confirm:")) {
      const name = step.slice("confirm:".length);
      return {
        reply: `في الطلب ${CONFIRM_LABEL[name] || "القيمة"}: ${displaySaved(name)}. نكمل بهالقيمة ولا تبي تغيّرها؟`,
        options: ["موافق", "تغيير"],
      };
    }
    if (step === "bank") {
      return { reply: "جهة التمويل؟", options: activeBanks().map((bank) => labelOf(bank, ["value", "name_ar", "text"])) };
    }
    if (step === "salary") return { reply: "راتب العميل كام؟", options: [] };
    if (step === "supported") return { reply: "هل مدعوم؟", options: ["نعم", "لا"] };
    if (step === "support_amount") return { reply: "قسط الدعم كام؟", options: [] };
    if (step === "add_support") return { reply: "نضيف قسط الدعم على الراتب؟", options: ["نعم", "لا"] };
    if (step === "work") {
      return {
        reply: "عسكري ولا مدني؟",
        options: jobItems().map((job) => labelOf(job, ["value", "name_ar", "text"])),
      };
    }
    if (step === "rank") {
      return {
        reply: "رتبته ايه؟",
        options: rankItems().map((rank) => labelOf(rank, ["value", "text", "name_ar"])),
      };
    }
    if (step === "birth") return { reply: "تاريخ ميلاد العميل؟ مثال: 1405-03-12", options: [] };
    if (step === "hiring") return { reply: "تاريخ التعيين؟ مثال: 1430-01-01", options: [] };
    if (step === "finance_mix") return { reply: "التمويل عقاري فقط ولا شخصي وعقاري؟", options: ["عقاري فقط", "شخصي وعقاري"] };
    if (step === "years") return { reply: "مدة التمويل كام سنة؟", options: ["15", "20", "25", "30"] };
    if (step === "product") {
      return {
        reply: "نوع المنتج؟",
        options: productItems().map((item) => labelOf(item, ["text", "value", "name_ar"])).concat(["تخطي"]),
      };
    }
    if (step === "property_amount") return { reply: "قيمة العقار؟", options: ["تخطي"] };
    if (step === "property_status") return { reply: "العقار مكتمل ولا غير مكتمل؟", options: ["مكتمل", "غير مكتمل", "تخطي"] };
    if (step === "residence") return { reply: "مسكن أول ولا ثاني؟", options: ["مسكن أول", "مسكن ثاني", "تخطي"] };
    if (step === "obligations") return { reply: "عليه التزامات؟", options: ["نعم", "لا"] };
    if (step === "obligations_installment") return { reply: "قسط الالتزامات كام؟", options: [] };
    if (step === "obligations_months") return { reply: "كم شهر باقي على الالتزامات؟", options: [] };
    if (step === "confirm") return { reply: "أجيب النسب من الشيت وأحسب؟", options: ["احسب", "تعديل"] };
    return { reply: "", options: [] };
  }

  function resolveNext() {
    if (pending.has("bank")) return "confirm:bank";
    if (!filled(collected.bank_id)) return "bank";
    if (pending.has("salary")) return "confirm:salary";
    if (!filled(collected.salary)) return "salary";
    if (pending.has("supported")) return "confirm:supported";
    if (!filled(collected.residential_support) && collected.residential_support !== 0) return "supported";
    if (pending.has("support_amount")) return "confirm:support_amount";
    if (Number(collected.residential_support) === 1 && !filled(collected.support_amount) && collected.support_amount !== 0) {
      return "support_amount";
    }
    if (Number(collected.residential_support) === 1 && collected._addSupportAnswered !== true) return "add_support";
    if (pending.has("work")) return "confirm:work";
    if (!filled(collected.job_position)) return "work";
    if (pending.has("rank")) return "confirm:rank";
    if (Number(collected.job_position) === 1 && !filled(collected.military_rank)) return "rank";
    if (pending.has("birth")) return "confirm:birth";
    if (!filled(collected.birthDate)) return "birth";
    if (pending.has("hiring")) return "confirm:hiring";
    if (Number(collected.job_position) !== 3 && !filled(collected.hiringDate)) return "hiring";
    if (needsFinanceMix() && !collected.finance_mix) return "finance_mix";
    if (!filled(collected.finance_duration)) return "years";
    if (pending.has("product")) return "confirm:product";
    if (!collected._skipProduct && !filled(collected.product_code)) return "product";
    if (pending.has("property_amount")) return "confirm:property_amount";
    if (!collected._skipProperty && !filled(collected.property_amount)) return "property_amount";
    if (pending.has("property_status")) return "confirm:property_status";
    if (!collected._skipPropertyStatus && !filled(collected.property_completed) && collected.property_completed !== 0) {
      return "property_status";
    }
    if (pending.has("residence")) return "confirm:residence";
    if (!collected._skipResidence && !filled(collected.residence_type)) return "residence";
    if (pending.has("obligations")) return "confirm:obligations";
    if (!collected._skipObligations && !filled(collected.has_obligations) && collected.has_obligations !== 0) {
      return "obligations";
    }
    if (pending.has("obligations_installment")) return "confirm:obligations_installment";
    if (Number(collected.has_obligations) === 1 && !filled(collected.obligations_installment) && collected.obligations_installment !== 0) {
      return "obligations_installment";
    }
    if (pending.has("obligations_months")) return "confirm:obligations_months";
    if (Number(collected.has_obligations) === 1 && !filled(collected.remaining_obligations_months) && collected.remaining_obligations_months !== 0) {
      return "obligations_months";
    }
    return "fetch";
  }

  function askNext(preface) {
    current = resolveNext();
    if (current === "fetch") {
      phase = "fetch";
      return {
        reply: preface || "تمام، بجيب النسب من شيت سياسات المنتجات البنكية وأحسب.",
        action: "fetch_factors",
        options: [],
        fields: publicFields(),
      };
    }
    const nextQuestion = question(current);
    return {
      reply: [preface, nextQuestion.reply].filter(Boolean).join("\n"),
      action: "ask",
      options: nextQuestion.options.filter(Boolean),
      fields: publicFields(),
    };
  }

  function clarify(message, options) {
    return {
      reply: message,
      action: "ask",
      options,
      fields: publicFields(),
    };
  }

  function applyWork(jobId, sourceText) {
    collected.job_position = Number(jobId);
    collected.retirment_age = RETIREMENT_AGE[Number(jobId)] || collected.retirment_age || 60;
    if (Number(jobId) !== 1) collected.military_rank = null;
    if (Number(jobId) === 1 && sourceText) {
      const ranks = matchCatalog(sourceText, rankItems(), (rank) => labelOf(rank, ["value", "text", "name_ar"]));
      if (ranks.length === 1) collected.military_rank = ranks[0].item.id;
    }
  }

  function displaySaved(step) {
    if (step === "bank") return nameById(activeBanks(), collected.bank_id);
    if (step === "salary") {
      const basic = filled(collected.basicSalary) ? ` (الأساسي ${collected.basicSalary})` : "";
      return `${collected.salary}${basic}`;
    }
    if (step === "supported") return Number(collected.residential_support) === 1 ? "مدعوم" : "غير مدعوم";
    if (step === "support_amount") return collected.support_amount;
    if (step === "work") return nameById(jobItems(), collected.job_position);
    if (step === "rank") return nameById(rankItems(), collected.military_rank);
    if (step === "birth") return collected.birthDate;
    if (step === "hiring") return collected.hiringDate;
    if (step === "product") {
      const found = productItems().find((item) => String(item.code != null && item.code !== "" ? item.code : item.id) === String(collected.product_code));
      return found ? labelOf(found, ["text", "value", "name_ar"]) : collected.product_code;
    }
    if (step === "property_amount") return collected.property_amount;
    if (step === "property_status") return Number(collected.property_completed) === 1 ? "مكتمل" : "غير مكتمل";
    if (step === "residence") return Number(collected.residence_type) === 2 ? "مسكن ثاني" : "مسكن أول";
    if (step === "obligations") return Number(collected.has_obligations) === 1 ? "نعم" : "لا";
    if (step === "obligations_installment") return collected.obligations_installment;
    if (step === "obligations_months") return collected.remaining_obligations_months;
    return "";
  }

  function clearStep(step) {
    (STEP_KEYS[step] || []).forEach((key) => {
      collected[key] = null;
    });
    pending.delete(step);
    if (step === "supported") {
      collected._addSupportAnswered = false;
      pending.delete("support_amount");
    }
    if (step === "work") {
      collected.military_rank = null;
      pending.delete("rank");
    }
    if (step === "product") collected._skipProduct = false;
    if (step === "property_amount") collected._skipProperty = false;
    if (step === "property_status") collected._skipPropertyStatus = false;
    if (step === "residence") collected._skipResidence = false;
    if (step === "obligations") {
      collected._skipObligations = false;
      pending.delete("obligations_installment");
      pending.delete("obligations_months");
    }
  }

  function answerConfirm(text) {
    const step = String(current).slice("confirm:".length);
    if (wantsCalculate(text)) {
      pending.clear();
      return askNext("تمام، بنستخدم القيم المحفوظة ونكمل.");
    }
    if (isAgree(text)) {
      pending.delete(step);
      if (step === "supported" && Number(collected.residential_support) !== 1) pending.delete("support_amount");
      if (step === "work" && Number(collected.job_position) === 3) pending.delete("hiring");
      if (step === "work" && Number(collected.job_position) !== 1) pending.delete("rank");
      if (step === "obligations" && Number(collected.has_obligations) !== 1) {
        pending.delete("obligations_installment");
        pending.delete("obligations_months");
        collected.obligations_installment = null;
        collected.remaining_obligations_months = null;
      }
      return askNext();
    }
    if (isChange(text)) {
      clearStep(step);
      return askNext();
    }
    pending.delete(step);
    clearStep(step);
    current = step;
    return answer(text);
  }

  function answer(text) {
    if (current === "bank") {
      const matches = matchCatalog(text, activeBanks(), (bank) => labelOf(bank, ["value", "name_ar", "text"]));
      if (matches.length !== 1) {
        return clarify("ما لقيت جهة تمويل واحدة بهالاسم. اختر من القائمة.", matches.map((match) => match.label));
      }
      collected.bank_id = matches[0].item.id;
      return askNext(`تمام، ${matches[0].label}.`);
    }
    if (current === "salary") {
      const amount = parseAmount(text);
      if (!amount || amount <= 0) return clarify("اكتب صافي الراتب رقمًا، مثل: 12000 أو 12 الف.", []);
      collected.salary = amount;
      const basicHint = normalizeArabic(text);
      if (basicHint.includes("اساس") && basicHint.includes("صاف")) {
        const numbers = normalizeDigits(text).match(/\d+(?:\.\d+)?/g) || [];
        if (numbers.length >= 2) collected.basicSalary = Number(numbers[0]);
      }
      return askNext(`الراتب ${amount}.`);
    }
    if (current === "supported") {
      if (!isYes(text) && !isNo(text)) return clarify("هل العميل مدعوم؟", ["نعم", "لا"]);
      collected.residential_support = isYes(text) ? 1 : 0;
      if (!isYes(text)) {
        collected.support_amount = 0;
        collected.add_support_installment_to_salary = 0;
        collected._addSupportAnswered = true;
      }
      return askNext();
    }
    if (current === "support_amount") {
      const amount = parseAmount(text);
      if (amount === null || amount < 0) return clarify("قسط الدعم كام؟", []);
      collected.support_amount = amount;
      return askNext();
    }
    if (current === "add_support") {
      if (!isYes(text) && !isNo(text)) return clarify("نضيف قسط الدعم على الراتب؟", ["نعم", "لا"]);
      collected.add_support_installment_to_salary = isYes(text) ? 1 : 0;
      collected._addSupportAnswered = true;
      return askNext();
    }
    if (current === "work") {
      const job = matchWork(text, jobItems());
      if (job.ambiguous) return clarify("قطاع خاص معتمد ولا غير معتمد؟", ["قطاع خاص معتمد", "قطاع خاص غير معتمد"]);
      if (!job.id) return clarify("عسكري ولا مدني؟ تقدر تختار من القائمة.", question("work").options);
      applyWork(job.id, text);
      const jobName = labelOf(job.item || { value: job.label }, ["value", "name_ar", "text"]) || job.label;
      return askNext(`تمام، ${jobName}. عمر التقاعد ${collected.retirment_age}.`);
    }
    if (current === "rank") {
      const matches = matchCatalog(text, rankItems(), (rank) => labelOf(rank, ["value", "text", "name_ar"]));
      if (matches.length !== 1) return clarify("ما لقيت الرتبة. اخترها من القائمة.", matches.map((match) => match.label));
      collected.military_rank = matches[0].item.id;
      return askNext(`الرتبة ${matches[0].label}.`);
    }
    if (current === "birth" || current === "hiring") {
      const parsed = parseDateAnswer(text);
      if (!parsed) return clarify("اكتب التاريخ هجري مثل 1405-03-12.", []);
      const target = current === "birth" ? "birth" : "hiring";
      collected[`${target}Date`] = parsed.hijri;
      collected[`${target}DateMiladi`] = parsed.miladi;
      if (parsed.date_type === 2) {
        collected.date_type = 2;
        if (collected.currentDate) {
          const today = moment(collected.currentDate, "iYYYY-iMM-iDD");
          if (today.isValid()) collected.currentDateMiladi = formatLatin(today, "YYYY-MM-DD");
        }
      }
      return askNext();
    }
    if (current === "finance_mix") {
      const normalized = normalizeArabic(text);
      if (normalized.includes("فقط")) collected.finance_mix = "real_only";
      else if (normalized.includes("شخصي") || normalized.includes("الاثنين") || isYes(text)) collected.finance_mix = "both";
      else return clarify("عقاري فقط ولا شخصي وعقاري؟", ["عقاري فقط", "شخصي وعقاري"]);
      return askNext();
    }
    if (current === "years") {
      const years = parseAmount(text);
      if (!years || years < 1 || years > 40) return clarify("مدة التمويل بالسنوات، مثل: 25", ["15", "20", "25", "30"]);
      collected.finance_duration = years;
      return askNext();
    }
    if (current === "product") {
      if (isSkip(text)) {
        collected._skipProduct = true;
        return askNext();
      }
      const matches = matchCatalog(text, productItems(), (item) => labelOf(item, ["text", "value", "name_ar"]));
      if (matches.length !== 1) return clarify("نوع المنتج؟", question("product").options);
      const item = matches[0].item;
      collected.product_code = item.code != null && item.code !== "" ? item.code : item.id;
      return askNext();
    }
    if (current === "property_amount") {
      if (isSkip(text)) {
        collected._skipProperty = true;
        return askNext();
      }
      const amount = parseAmount(text);
      if (!amount || amount <= 0) return clarify("قيمة العقار رقمًا، أو قل تخطي.", ["تخطي"]);
      collected.property_amount = amount;
      return askNext();
    }
    if (current === "property_status") {
      if (isSkip(text)) {
        collected._skipPropertyStatus = true;
        return askNext();
      }
      const normalized = normalizeArabic(text);
      if (normalized.includes("غير مكتمل")) collected.property_completed = 0;
      else if (normalized.includes("مكتمل")) collected.property_completed = 1;
      else return clarify("مكتمل ولا غير مكتمل؟", ["مكتمل", "غير مكتمل"]);
      return askNext();
    }
    if (current === "residence") {
      if (isSkip(text)) {
        collected._skipResidence = true;
        return askNext();
      }
      const normalized = normalizeArabic(text);
      if (normalized.includes("ثاني")) collected.residence_type = 2;
      else if (normalized.includes("اول")) collected.residence_type = 1;
      else return clarify("مسكن أول ولا ثاني؟", ["مسكن أول", "مسكن ثاني"]);
      return askNext();
    }
    if (current === "obligations") {
      if (!isYes(text) && !isNo(text)) return clarify("عليه التزامات؟", ["نعم", "لا"]);
      const on = isYes(text) ? 1 : 0;
      collected.has_obligations = on;
      collected.hasObligations = on;
      if (!on) {
        collected.obligations_installment = null;
        collected.remaining_obligations_months = null;
      }
      return askNext();
    }
    if (current === "obligations_installment") {
      const amount = parseAmount(text);
      if (amount === null || amount < 0) return clarify("قسط الالتزامات كام؟", []);
      collected.obligations_installment = amount;
      return askNext();
    }
    if (current === "obligations_months") {
      const months = parseAmount(text);
      if (months === null || months < 0) return clarify("كم شهر باقي؟", []);
      collected.remaining_obligations_months = months;
      return askNext();
    }
    return askNext();
  }

  function handle(text) {
    const value = String(text || "").trim();
    if (!value) return askNext();
    if (String(current).startsWith("confirm:")) return answerConfirm(value);
    if (current === "confirm") {
      if (isYes(value) || wantsCalculate(value)) return askNext();
      return startEdit(value) || clarify("قل احسب، أو اختر وش تعدل.", ["احسب", "تعديل"]);
    }
    if (phase === "done" || phase === "fetch") {
      const edited = startEdit(value);
      if (edited) return edited;
      if (wantsCalculate(value) || isYes(value)) return askNext("أعيد جلب النسب وأحسب.");
      return clarify("تبي تعدل إجابة، ولا أحسب من جديد؟", ["تعديل", "احسب"]);
    }
    if (wantsCalculate(value) && current !== "salary" && current !== "support_amount" && current !== "property_amount" && current !== "obligations_installment" && current !== "obligations_months") {
      collected._skipProduct = true;
      collected._skipProperty = true;
      collected._skipPropertyStatus = true;
      collected._skipResidence = true;
      if (!filled(collected.has_obligations) && collected.has_obligations !== 0) {
        collected._skipObligations = true;
        collected.has_obligations = 0;
        collected.hasObligations = 0;
      }
      if (Number(collected.residential_support) === 1 && collected._addSupportAnswered !== true) {
        collected.add_support_installment_to_salary = 0;
        collected._addSupportAnswered = true;
      }
      const next = askNext();
      if (next.action === "ask") {
        return { ...next, reply: `قبل الحساب لازم:\n${next.reply}` };
      }
      return next;
    }
    if (isSkip(value) && ["product", "property_amount", "property_status", "residence"].includes(current)) {
      return answer(value);
    }
    return answer(value);
  }

  function startEdit(text) {
    const normalized = normalizeLoose(text);
    if (normalized === "تعديل") {
      phase = "done";
      return clarify("وش تبي تعدل؟", ["البنك", "الراتب", "الدعم", "الوظيفة", "الرتبة", "الميلاد", "التعيين", "المنتج", "العقار", "الالتزامات", "مدة التمويل"]);
    }
    const durationChange = readDurationChange(text);
    if (durationChange) {
      if (!durationChange.years) {
        collected.finance_duration = null;
        phase = "ask";
        return askNext("تمام، نعدل مدة التمويل.");
      }
      collected.finance_duration = durationChange.years;
      phase = "fetch";
      return {
        reply: `تمام، مدة التمويل ${durationChange.years} سنة. بجيب النسب من الشيت وأحسب.`,
        action: "fetch_factors",
        options: [],
        fields: publicFields(),
      };
    }
    const edits = [
      { test: /بنك|جهه التمويل/, clear: ["bank_id"], label: "جهة التمويل" },
      { test: /راتب/, clear: ["salary"], label: "الراتب" },
      { test: /دعم/, clear: ["residential_support", "support_amount", "_addSupportAnswered"], label: "الدعم" },
      { test: /وظيف|عسكري|مدني|متقاعد/, clear: ["job_position", "military_rank"], label: "جهة العمل" },
      { test: /رتب/, clear: ["military_rank"], label: "الرتبة" },
      { test: /ميلاد/, clear: ["birthDate", "birthDateMiladi"], label: "تاريخ الميلاد" },
      { test: /تعيين/, clear: ["hiringDate", "hiringDateMiladi"], label: "تاريخ التعيين" },
      { test: /منتج/, clear: ["product_code", "_skipProduct"], label: "المنتج" },
      { test: /عقار/, clear: ["property_amount", "property_completed", "_skipProperty", "_skipPropertyStatus"], label: "العقار" },
      { test: /التزام/, clear: ["has_obligations", "hasObligations", "obligations_installment", "remaining_obligations_months", "_skipObligations"], label: "الالتزامات" },
      { test: /عقاري فقط|شخصي وعقاري/, clear: ["finance_mix"], label: "نوع التمويل" },
    ];
    const edit = edits.find((item) => item.test.test(normalized));
    if (!edit) return null;
    edit.clear.forEach((key) => {
      collected[key] = null;
      Object.keys(STEP_KEYS).forEach((step) => {
        if ((STEP_KEYS[step] || []).includes(key)) pending.delete(step);
      });
    });
    phase = "ask";
    return askNext(`تمام، نعدل ${edit.label}.`);
  }

  function start() {
    const preface = pending.size
      ? "أهلًا. فيه بيانات محفوظة في الطلب، أأكدها معك واحدة واحدة، وبعدين أجيب النسب من الشيت وأحسب."
      : "أهلًا، بنحسب التمويل سؤال سؤال، وبعدها أجيب النسب من الشيت وأحسب.";
    const first = askNext(preface);
    if (first.action === "fetch_factors") {
      current = "confirm";
      phase = "ask";
      return {
        reply: `${preface}\nالبيانات مكتملة. أجيب النسب من الشيت وأحسب؟`,
        action: "ask",
        options: ["احسب", "تعديل"],
        fields: publicFields(),
      };
    }
    return first;
  }

  function markDone() {
    phase = "done";
  }

  function setCatalogs(next) {
    if (next.banks) catalogs.banks = next.banks;
    if (next.jobs) catalogs.jobs = next.jobs;
    if (next.ranks) catalogs.ranks = next.ranks;
    if (next.products) catalogs.products = next.products;
  }

  function setFinanceDuration(years) {
    collected.finance_duration = years;
    phase = "done";
  }

  function questionForCurrent() {
    return current ? question(current) : null;
  }

  return {
    start,
    handle,
    markDone,
    setCatalogs,
    setFinanceDuration,
    questionForCurrent,
    publicFields,
    formFields,
    get currentStep() {
      return current;
    },
  };

  function blockedKeys() {
    const blocked = new Set();
    pending.forEach((step) => {
      (STEP_KEYS[step] || []).forEach((key) => blocked.add(key));
    });
    return blocked;
  }

  function collectFields(respectPending) {
    const keys = [
      "bank_id",
      "salary",
      "basicSalary",
      "job_position",
      "military_rank",
      "residential_support",
      "support_amount",
      "add_support_installment_to_salary",
      "birthDate",
      "birthDateMiladi",
      "hiringDate",
      "hiringDateMiladi",
      "currentDate",
      "currentDateMiladi",
      "date_type",
      "retirment_age",
      "product_code",
      "property_amount",
      "property_completed",
      "residence_type",
      "has_obligations",
      "hasObligations",
      "obligations_installment",
      "remaining_obligations_months",
      "finance_duration",
      "finance_mix",
    ];
    const blocked = respectPending ? blockedKeys() : new Set();
    const fields = {};
    keys.forEach((key) => {
      if (collected[key] === undefined) return;
      if (blocked.has(key)) return;
      fields[key] = collected[key];
    });
    return fields;
  }

  function publicFields() {
    return collectFields(true);
  }

  function formFields() {
    return collectFields(false);
  }

  function nameById(items, id) {
    const found = items.find((item) => String(item.id) === String(id));
    return found ? labelOf(found, ["value", "text", "name_ar", "name"]) : id;
  }

  function needsFinanceMix() {
    const bank = (catalogs.banks || []).find((item) => String(item.id) === String(collected.bank_id));
    const name = normalizeArabic(bank ? labelOf(bank, ["value", "name_ar", "text"]) : "");
    return name.includes("ساب") || name.includes("انماء");
  }
}

function seed(known, todayHijri, saved = {}) {
  const source = { ...(known || {}) };
  Object.keys(saved || {}).forEach((key) => {
    const current = source[key];
    const empty = current === null || current === undefined || current === "" || (typeof current === "number" && Number.isNaN(current));
    if (empty) source[key] = saved[key];
  });
  const collected = {
    date_type: source.date_type || 1,
    currentDate: source.currentDate || todayHijri || "",
  };
  const copy = [
    "bank_id",
    "salary",
    "basicSalary",
    "job_position",
    "military_rank",
    "birthDate",
    "birthDateMiladi",
    "hiringDate",
    "hiringDateMiladi",
    "product_code",
    "property_amount",
    "property_completed",
    "residence_type",
    "obligations_installment",
    "remaining_obligations_months",
    "retirment_age",
  ];
  copy.forEach((key) => {
    if (filled(source[key]) || source[key] === 0) collected[key] = source[key];
  });
  if (filled(source.residential_support) || source.residential_support === 0) {
    collected.residential_support = source.residential_support;
  }
  if (Object.prototype.hasOwnProperty.call(saved, "has_obligations") || Object.prototype.hasOwnProperty.call(saved, "hasObligations")) {
    const on = Number(saved.has_obligations != null ? saved.has_obligations : saved.hasObligations) === 1 ? 1 : 0;
    collected.has_obligations = on;
    collected.hasObligations = on;
  } else if (Number(source.hasObligations) === 1 || Number(source.has_obligations) === 1) {
    collected.has_obligations = 1;
    collected.hasObligations = 1;
  }
  if (Number(collected.job_position) === 3) {
    collected.retirment_age = RETIREMENT_AGE[3];
  } else if (filled(collected.job_position) && !filled(collected.retirment_age)) {
    collected.retirment_age = RETIREMENT_AGE[Number(collected.job_position)] || 60;
  }
  if (Number(collected.residential_support) === 0) {
    collected.support_amount = collected.support_amount || 0;
    collected.add_support_installment_to_salary = 0;
    collected._addSupportAnswered = true;
  }
  return collected;
}

function matchWork(text, jobs) {
  const normalized = normalizeArabic(text);
  const byName = (id) => jobs.find((job) => Number(job.id) === id);
  if (normalized.includes("متقاعد")) return packJob(byName(3), 3, "متقاعد");
  if (normalized.includes("شبه")) return packJob(byName(4), 4, "شبه حكومي");
  if (normalized.includes("غير معتمد")) return packJob(byName(5), 5, "قطاع خاص غير معتمد");
  if (normalized.includes("معتمد") && normalized.includes("خاص")) return packJob(byName(6), 6, "قطاع خاص معتمد");
  if (normalized.includes("خاص")) return { ambiguous: true };
  if (normalized.includes("عسكري")) return packJob(byName(1), 1, "عسكري");
  if (normalized.includes("مدني")) return packJob(byName(2), 2, "مدني");
  const matches = matchCatalog(text, jobs, (job) => labelOf(job, ["value", "name_ar", "text"]));
  if (matches.length === 1) return packJob(matches[0].item, matches[0].item.id, matches[0].label);
  return {};
}

function packJob(item, id, label) {
  return { id: item ? item.id : id, item, label: item ? labelOf(item, ["value", "name_ar", "text"]) : label };
}
